// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useState, type ReactNode } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import type { PlayingStyleTraitKey } from './types';

/**
 * How far a bar moves from the middle line per unit of trait. A personal profile is pulled towards
 * the average golfer (0.5) by design, so real players sit within about 0.35–0.65 and would all
 * look alike at 1:1; tripling the distance spreads them over the bar. A tour profile is already
 * a place in the field running the full 0–1. Display scale only: the values themselves are the
 * service's.
 */
export const BAR_STRETCH = { personal: 3, tour: 1 } as const;

/** The shortest bar still shows as a stub, so "least of all" never reads as "no bar". */
const MIN_BAR = 0.03;

/**
 * A trait's name with a (?) that opens what it means, above whatever bars show it. `testIDPrefix`
 * keeps two views of the same trait on one screen (the leaderboard's board and its popup) apart.
 */
export function TraitBlock({
  traitKey,
  testIDPrefix,
  children,
}: {
  traitKey: PlayingStyleTraitKey;
  testIDPrefix: string;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const [helpOpen, setHelpOpen] = useState(false);
  const label = t(`playingStyle.trait.${traitKey}.label`);

  return (
    <View style={styles.block} testID={`${testIDPrefix}-trait-${traitKey}`}>
      <View style={styles.header}>
        <Text style={styles.label}>{label}</Text>
        <Pressable
          style={[styles.helpButton, helpOpen && styles.helpButtonOpen]}
          onPress={() => setHelpOpen((open) => !open)}
          accessibilityRole="button"
          accessibilityLabel={t('playingStyle.helpButton', { trait: label })}
          aria-expanded={helpOpen}
          hitSlop={8}
          testID={`${testIDPrefix}-help-${traitKey}`}
        >
          {/* eslint-disable-next-line i18n/no-literal-text -- a universal help glyph, named by accessibilityLabel */}
          <Text style={[styles.helpButtonText, helpOpen && styles.helpButtonTextOpen]}>?</Text>
        </Pressable>
      </View>
      {children}
      {helpOpen ? (
        <Text style={styles.help} testID={`${testIDPrefix}-help-text-${traitKey}`}>
          {t(`playingStyle.trait.${traitKey}.help`)}
        </Text>
      ) : null}
    </View>
  );
}

const TRACK_HEIGHT = 10;

/** The box a `tip` is centred in, at the end of the fill; half of it overhangs a full bar. */
export const BAR_TIP_SIZE = 16;

/**
 * One bar, with the middle line (the average golfer, or the middle of the field) marked. `color`
 * tells one player's bar from another's; `tip` is drawn centred on the end of the fill.
 */
export function TraitBar({
  value,
  stretch,
  color = appColors.accent.primary,
  tip,
  testID,
}: {
  value: number;
  stretch: number;
  color?: string;
  tip?: ReactNode;
  testID?: string;
}) {
  const fraction = Math.max(MIN_BAR, Math.min(1, 0.5 + (value - 0.5) * stretch));
  const width = `${fraction * 100}%` as const;

  return (
    <View style={styles.bar}>
      <View style={styles.track}>
        <View style={[styles.fill, { width, backgroundColor: color }]} testID={testID} />
        <View style={styles.midline} />
      </View>
      {tip ? <View style={[styles.tip, { left: width }]}>{tip}</View> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  block: {
    gap: 6,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.secondary,
  },
  helpButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.text.muted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  helpButtonOpen: {
    borderColor: appColors.accent.primary,
    backgroundColor: appColors.accent.primary,
  },
  helpButtonText: {
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 14,
    color: appColors.text.muted,
  },
  helpButtonTextOpen: {
    color: appColors.background.panel,
  },
  bar: {
    height: TRACK_HEIGHT,
  },
  track: {
    height: TRACK_HEIGHT,
    borderRadius: TRACK_HEIGHT / 2,
    backgroundColor: appColors.background.panelAlt,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    borderRadius: TRACK_HEIGHT / 2,
  },
  // Outside the track, which clips, so the tip can stand taller than the bar.
  tip: {
    position: 'absolute',
    top: (TRACK_HEIGHT - BAR_TIP_SIZE) / 2,
    width: BAR_TIP_SIZE,
    height: BAR_TIP_SIZE,
    marginLeft: -BAR_TIP_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // The average golfer, so every bar reads against the same mark.
  midline: {
    position: 'absolute',
    left: '50%',
    top: -1,
    bottom: -1,
    width: 2,
    marginLeft: -1,
    backgroundColor: appColors.text.heading,
  },
  help: {
    fontSize: 12.5,
    lineHeight: 18,
    color: appColors.text.secondary,
  },
});
